import { createElement, lazy, Suspense, ComponentType } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLoader } from 'routes/PrivateRouter';
import { reportsHubURLs } from 'routes/reports/reportsHubURLs';

import { PermissionGuard } from 'components/PermissionGuard';

// Lazy load all components
const ReportsHub = lazy(() =>
  import('pages/reporting/reports-hub/ReportsHub').then((m) => ({
    default: m.ReportsHub,
  }))
);
const AnalysisCreateView = lazy(() =>
  import('pages/reporting/reports-hub/financial-analysis/create-new/AnalysisCreateView').then(
    (m) => ({ default: m.AnalysisCreateView })
  )
);
const NewAnalysisPreview = lazy(() =>
  import('pages/reporting/reports-hub/financial-analysis/demo/NewAnalysisPreview').then(
    (m) => ({ default: m.NewAnalysisPreview })
  )
);
const AnalysisEditView = lazy(() =>
  import('pages/reporting/reports-hub/financial-analysis/edit/AnalysisEditView').then(
    (m) => ({ default: m.AnalysisEditView })
  )
);
const AnalysisDetails = lazy(() =>
  import('pages/reporting/reports-hub/financial-analysis/view/AnalysisDetails').then(
    (m) => ({ default: m.AnalysisDetails })
  )
);
const CreateNewReport = lazy(() =>
  import('pages/reporting/reports-hub/financial-statements/CreateNewReport').then(
    (m) => ({ default: m.CreateNewReport })
  )
);
const ReportDetails = lazy(() =>
  import('pages/reporting/reports-hub/financial-statements/ReportDetails').then(
    (m) => ({
      default: m.ReportDetails,
    })
  )
);
const DemoReportDetails = lazy(() =>
  import('pages/reporting/reports-hub/financial-statements/demo/DemoReportDetails').then(
    (m) => ({ default: m.DemoReportDetails })
  )
);
const CashFlowForecast = lazy(
  () =>
    import('pages/reporting/reports-hub/financial-statements/demo/cashFlowForecast/CashFlowForecast')
);
const CashFlowIntakeForm = lazy(
  () =>
    import('pages/reporting/reports-hub/financial-statements/demo/cashFlowForecast/CashFlowIntakeForm')
);
const BudgetsVsActuals = lazy(() =>
  import('pages/reporting/reports-hub/financial-statements/budgets-vs-actuals').then(
    (m) => ({ default: m.BudgetsVsActuals })
  )
);
const SaaSMetricsPage = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/saas-metrics/SaasMetricsTableView').then(
    (m) => ({
      default: m.SaaSMetricsPage,
    })
  )
);
const RevenueEfficiencyMetricsPage = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/revenue-efficiency-metrics/RevenueEfficiencyMetricsTableView').then(
    (m) => ({
      default: m.RevenueEfficiencyMetricsPage,
    })
  )
);
const ExecutiveReportDemo = lazy(
  () =>
    import('pages/reporting/reports-hub/kpi-metrics/demo/executive-report/ExecutiveReportDemo')
);

const WeeklyCoversReport = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/demo/weekly-covers/WeeklyCoversReport').then(
    (m) => ({
      default: m.WeeklyCoversReport,
    })
  )
);
const DailyNetSalesReport = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/demo/daily-net-sales/DailyNetSalesReport').then(
    (m) => ({
      default: m.DailyNetSalesReport,
    })
  )
);
const WeeklyOperationalAnalyticsReport = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/demo/weekly-operational-analytics/WeeklyOperationalAnalyticsReport').then(
    (m) => ({
      default: m.WeeklyOperationalAnalyticsReport,
    })
  )
);
const LaborDistributionReport = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/demo/labor-distribution-analysis/LaborDistributionReport').then(
    (m) => ({
      default: m.LaborDistributionReport,
    })
  )
);
const SigmaReport = lazy(() =>
  import('pages/reporting/reports-hub/kpi-metrics/demo/sigma-report/SigmaReport').then(
    (m) => ({
      default: m.SigmaReport,
    })
  )
);

const routeMap: Record<string, ComponentType> = {
  [reportsHubURLs.boardDecks.base]: () => <ReportsHub tab="board-decks" />,
  [reportsHubURLs.financialAnalysis.base]: () => (
    <ReportsHub tab="financial-analysis" />
  ),
  [reportsHubURLs.financialStatements.base]: () => (
    <ReportsHub tab="financial-statements" />
  ),
  [reportsHubURLs.closeDeliverables.base]: () => (
    <ReportsHub tab="close-deliverables" />
  ),
  [reportsHubURLs.kpiMetrics.base]: () => <ReportsHub tab="kpi-metrics" />,
  [reportsHubURLs.financialAnalysis.new]: AnalysisCreateView,
  [reportsHubURLs.financialAnalysis.edit({ id: ':id' })]: AnalysisEditView,
  [reportsHubURLs.financialAnalysis.view({ id: ':id' })]: AnalysisDetails,
  [reportsHubURLs.financialAnalysis.preview({})]: NewAnalysisPreview,
  [reportsHubURLs.financialStatements.demo]: DemoReportDetails,
  [reportsHubURLs.financialStatements.view({
    report_type: ':report_type',
    id: ':id',
  })]: ReportDetails,
  [reportsHubURLs.financialStatements.create]: CreateNewReport,
  [reportsHubURLs.financialStatements.editDraft({ id: ':id' })]:
    CreateNewReport,
  [reportsHubURLs.financialStatements.budgetsVsActuals({ id: ':id' })]:
    BudgetsVsActuals,
  [reportsHubURLs.cashFlowForecast.base]: CashFlowForecast,
  [reportsHubURLs.cashFlowForecast.intakeForm]: CashFlowIntakeForm,
  [reportsHubURLs.kpiMetrics.saasMetrics]: SaaSMetricsPage,
  [reportsHubURLs.kpiMetrics.revenueEfficiencyMetrics]:
    RevenueEfficiencyMetricsPage,
  [reportsHubURLs.kpiMetrics.executiveReportDemo]: ExecutiveReportDemo,
  [reportsHubURLs.kpiMetrics.weeklyCovers]: WeeklyCoversReport,
  [reportsHubURLs.kpiMetrics.dailyNetSales]: DailyNetSalesReport,
  [reportsHubURLs.kpiMetrics.weeklyOperationalAnalytics]:
    WeeklyOperationalAnalyticsReport,
  [reportsHubURLs.kpiMetrics.laborDistributionAnalysis]:
    LaborDistributionReport,
  [reportsHubURLs.kpiMetrics.sigmaReport]: SigmaReport,
} as const;

export const reportsHubRoutes = [
  {
    path: reportsHubURLs._base_relative_path,
    children: [
      {
        index: true,
        element: (
          <Navigate to={reportsHubURLs.financialAnalysis.base} replace />
        ),
      },
      ...Object.entries(routeMap).map(([path, Component]) => ({
        path: path.replace(`${reportsHubURLs.base}/`, ''),
        element: (
          <PermissionGuard moduleKey="reports-hub">
            <Suspense fallback={<PageLoader />}>
              {createElement(Component)}
            </Suspense>
          </PermissionGuard>
        ),
      })),
    ],
  },
];
